import React, {useEffect, useMemo} from "react";
import {useQuery} from "@apollo/client";
import gql from "graphql-tag";
import {useHistory} from "react-router";
import ReactDocumentTitle from "../../../components/ReactDocumentTitle";
import AppLayout from "../../../layouts/AppLayout";
import {Attendee, AttendeeOrderByInput, EventType, ReportConfig} from "../../../schema";
import {SortingRule} from "react-table";
import Table, {TableFilter} from "../../../ui/table/Table";
import H1 from "../../../ui/H1";
import {reverse} from "../../../urls";
import {FilterState} from "../../../ui/table/TableFilter";
import Location from "../../../models/event/Location";
import ScheduleFormatter from "../../../models/formatter/ScheduleFormatter";
import Schedule from "../../../models/event/Schedule";
import IconFullStar from "../../../components/icon/IconFullStar";
import RatingBadge from "../../../components/attendee/evaluation/RatingBadge";
import {SelectFilterBlock, SelectValue} from "../../../ui/table/filter/UISelectFilterBlock";
import Modal from "../../../ui/modal/Modal";
import PrimaryButton from "../../../ui/PrimaryButton";
import {ExportAttendeeForm} from "../../../components/attendee/ExportAttendeeForm";
import ModalContent from "../../../ui/modal/ModalContent";

type TableData = {
  id: string
  name: string
  event: {
    title: React.ReactNode
    location: React.ReactNode
    schedule: React.ReactNode
  }
  rating?: React.ReactNode
}

function fromAttendee(attendee: Attendee): TableData {
  const rating = attendee.evaluation?.rating ?
    <RatingBadge id={`${attendee.id}_rating`} value={attendee.evaluation.rating}
                 caption={<>{attendee.evaluation.rating} <IconFullStar className="ml-0.5"/></>}/> : <></>
  return {
    id: attendee.id,
    name: attendee.name,
    event: {
      title: <span className="whitespace-normal">{attendee.event.title}</span>,
      location: <span className="whitespace-normal">{new Location(attendee.event.location).toString()}</span>,
      schedule: ScheduleFormatter.format('en', new Schedule(attendee.event.schedule), 'interval')
    },
    rating
  }
}

export default function BrandAttendeeListScene() {
  const history = useHistory();

  const skipPageResetRef = React.useRef<boolean>();
  const [attendees, setAttendees] = React.useState<TableData[]>([]);
  const [totalCount, setTotalCount] = React.useState<number>(1);
  const [skip, setSkip] = React.useState<number>(0);
  const [first, setFirst] = React.useState<number>(25);
  const [sortBy, setSortBy] = React.useState<AttendeeOrderByInput>(AttendeeOrderByInput.name_asc);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(undefined);
  const [skipDataLoad, setSkipDataLoad] = React.useState<boolean>(true);
  const [open, setOpen] = React.useState<boolean>(false);

  const [states, setStates] = React.useState<FilterState[]>([new FilterState('eventType', false, '')]);
  const selectedTypeId = states.find(x => x.id === 'eventType' && x.active)?.value

  const {data, loading, error} = useQuery<AttendeeQuery>(query, {
    skip: skipDataLoad,
    fetchPolicy: 'cache-and-network',
    variables: {skip, first, sortBy, searchQuery, filterByTypeId: selectedTypeId},
  });

  useMemo(() => {
    if (data) {
      setAttendees(data.feed.attendees.map(x => fromAttendee(x)))
      setTotalCount(data.feed.total);
    }
  }, [data])

  const onFetchData = (pageIndex: number, newPageSize: number, newSortBy: SortingRule<Attendee>[], filter: TableFilter | null) => {
    skipPageResetRef.current = true;
    setSkip(pageIndex * newPageSize);
    setFirst(newPageSize);
    const value = newSortBy.find((x) => x.id === 'name')?.desc;
    setSearchQuery(filter?.search);
    setSortBy(value ? AttendeeOrderByInput.name_desc : AttendeeOrderByInput.name_asc);
    setStates(filter?.filters || []);
    setSkipDataLoad(false);
  }

  const onClick = (attendee: TableData) => {
    history.push(reverse('attendees_detail', {id: attendee.id}));
  }

  useEffect(() => {
    skipPageResetRef.current = false;
  }, []);

  const values: SelectValue[] = data?.types.map(x => ({id: x.id, name: x.name})) || [];
  const typeFilter = new SelectFilterBlock('eventType', 'Event Type', values);

  const columns = React.useMemo(() => [
    {
      Header: 'Attendees',
      columns: [
        {
          Header: 'Name',
          accessor: 'name',
          filter: 'fuzzyText',
          defaultSort: 'desc',
          sortType: 'basic',
        },
        {
          Header: 'Event',
          accessor: 'event.title',
          disableSortBy: true,
        },
        {
          Header: 'Location',
          accessor: 'event.location',
          disableSortBy: true,
        },
        {
          Header: 'Dates',
          accessor: 'event.schedule',
          disableSortBy: true,
        },
        {
          Header: 'Rating',
          accessor: 'rating',
          disableSortBy: true,

        }]
    }
  ].flatMap((c: any) => c.columns), []);

  const buttons =
    <PrimaryButton onClick={() => setOpen(true)}>Export</PrimaryButton>;

  return (
    <>
      <ReactDocumentTitle title='Attendees'/>

      <AppLayout className="">
        <div className="pt-8 pb-20">
          <H1>Attendees</H1>
          <div className="mt-6 mb-4">
            <Table<TableData, TableFilter> name="attendees-v2" reset={skipPageResetRef.current || false}
                                           error={error?.message}
                                           loading={loading}
                                           total={totalCount}
                                           onFetchData={onFetchData} columns={columns}
                                           data={attendees}
                                           filters={[typeFilter]}
                                           buttons={buttons}
                                           onClick={onClick}/>
          </div>
          <Modal show={open} onClose={() => setOpen(false)} title="Export Attendees">
              {data?.reportConfig &&
                <ExportAttendeeForm config={data.reportConfig} onClose={() => setOpen(false)}/>
              }
          </Modal>
        </div>
      </AppLayout>
    </>
  )
}

type AttendeeFeed = {
  total: number;
  count: number;
  skip: number;
  attendees: Attendee[];
}
type AttendeeQuery = {
  feed: AttendeeFeed;
  types: EventType[];
  reportConfig: ReportConfig;
}


const query = gql`
  query AttendeeListQuery($searchQuery: String, $skip: Int!, $first: Int!, $sortBy: AttendeeOrderByInput!, $filterByTypeId: String) {
    feed: attendees(searchBy: $searchQuery, orderBy: $sortBy, skip: $skip, count: $first, filterByTypeId: $filterByTypeId) {
      total
      count
      skip

      attendees {
        id
        name
        status
        event {
          id
          title
          location {
            online
            city
            country
          }
          schedule {
            start
            end
            timezone
          }
        }
        evaluation {
          rating
        }
        created
      }
    }
    types: eventTypes {
      id
      name
    },
    reportConfig {
      event {
        id
        title
        selected
      },
      attendee {
        id
        title
        selected
      },
      evaluation {
        id
        title
        selected
      }
    }
  }
`;
